/**
 * EmbryoDetailSheet
 *
 * Unified full-screen modal detail view for an embryo across all lifecycle contexts.
 * Follows the SupplementDetailSheet pattern: onboarding background, glass card,
 * hero visual at top, title with nickname, badge row, conditional sections.
 */

import React, { useCallback } from "react";
import { View, Text, Image, Pressable, Modal } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import Svg, { Rect, Line, Circle, Text as SvgText, G } from "react-native-svg";
import {
  X,
  Dna,
  FlaskConical,
  Info,
  Snowflake,
  Calendar,
  HeartPulse,
  Baby,
  Pencil,
} from "lucide-react-native";
import {
  StatusBadge,
  Tag,
  useScrollHaptics,
  springs,
  type StatusBadgeTone,
} from "@louise/moon-ui";
import {
  EMBER,
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";
import SectionTitle from "@louise/moon-ui/primitives/SectionTitle";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import type {
  EmbryoDetailSheetProps,
  EmbryoDetailData,
  ChromosomeCN,
  EmbryoDevelopmentObservation,
  MonogenicFinding,
  PolygenicRisk,
} from "./embryo-detail.types";

// ── Color constants ──

const RESULT_COLORS = {
  euploid: "#4E7342",
  aneuploid: "#8B3A1A",
  mosaic: "#B08030",
  no_result: "#8A7D74",
} as const;

const RESULT_BG = {
  euploid: "rgba(78, 115, 66, 0.1)",
  aneuploid: "rgba(139, 58, 26, 0.1)",
  mosaic: "rgba(176, 128, 48, 0.1)",
  no_result: "rgba(138, 125, 116, 0.08)",
} as const;

const RESULT_LABELS: Record<keyof typeof RESULT_COLORS, string> = {
  euploid: "Euploid",
  aneuploid: "Aneuploid",
  mosaic: "Mosaic",
  no_result: "No result",
};

const RESULT_TONES: Record<keyof typeof RESULT_COLORS, StatusBadgeTone> = {
  euploid: "green",
  aneuploid: "coral",
  mosaic: "gold",
  no_result: "neutral",
};

const TRANSFER_LABELS = {
  yes: "Recommended",
  no: "Not recommended",
  with_counseling: "With counseling",
} as const;

const STATUS_LABELS: Record<string, string> = {
  frozen: "Frozen",
  transferred: "Transferred",
  discarded: "Discarded",
  donated: "Donated",
  research: "Donated to Research",
  thawed_refrozen: "Thawed & Refrozen",
};

const STATUS_TONES: Record<string, StatusBadgeTone> = {
  frozen: "blue",
  transferred: "green",
  discarded: "neutral",
  donated: "purple",
  research: "purple",
  thawed_refrozen: "gold",
};

const MONOGENIC_STATUS_TONES: Record<
  MonogenicFinding["status"],
  StatusBadgeTone
> = {
  unaffected: "green",
  carrier: "neutral",
  affected: "coral",
};

const OUTCOME_LABELS: Record<string, string> = {
  implanted: "Implanted",
  chemical: "Chemical Pregnancy",
  miscarriage: "Miscarriage",
  ongoing: "Ongoing Pregnancy",
  live_birth: "Live Birth",
  not_transferred: "Not Transferred",
};

const OUTCOME_COLORS: Record<string, string> = {
  implanted: "#4E7342",
  chemical: "#B08030",
  miscarriage: "#8B3A1A",
  ongoing: "#4E7342",
  live_birth: "#4E7342",
  not_transferred: "#8A7D74",
};

const TH = {
  fontSize: 10,
  fontWeight: "600" as const,
  color: EMBER.colors.textFaint,
  textTransform: "uppercase" as const,
  letterSpacing: 0.5,
};

// ── Shared sub-components ──

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <View
      style={{ flexDirection: "row", alignItems: "center", paddingVertical: 4 }}
    >
      <Text
        className="font-dm-sans"
        style={{ fontSize: 13, color: EMBER.colors.textLight, width: 110 }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: EMBER.colors.text,
          flex: 1,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

// ── Genetic Overview / Transfer Recommendation ──

function GeneticOverviewSection({ embryo }: { embryo: EmbryoDetailData }) {
  const hasData =
    embryo.result || embryo.karyotype || embryo.transferRecommendation;
  if (!hasData) return null;

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={Dna}>Genetic Overview</SectionTitle>
      <View style={{ gap: 4, marginTop: 6 }}>
        {embryo.karyotype && (
          <DetailRow label="Karyotype" value={embryo.karyotype} />
        )}
        {embryo.pgtPerformed && (
          <DetailRow label="PGT" value={embryo.pgtPerformed} />
        )}
        {embryo.biopsyDate && (
          <DetailRow label="Biopsy date" value={embryo.biopsyDate} />
        )}
        {embryo.biopsyDay && !embryo.biopsyDate && (
          <DetailRow label="Biopsy" value={`Day ${embryo.biopsyDay}`} />
        )}
      </View>

      {/* Transfer recommendation banner */}
      {embryo.transferRecommendation && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            paddingVertical: 8,
            paddingHorizontal: 12,
            borderRadius: 10,
            backgroundColor: embryo.result
              ? RESULT_BG[embryo.result]
              : "rgba(0,0,0,0.03)",
            marginTop: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            Transfer:
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "700",
              color:
                RESULT_COLORS[
                  embryo.transferRecommendation === "yes"
                    ? "euploid"
                    : embryo.transferRecommendation === "no"
                      ? "aneuploid"
                      : "mosaic"
                ],
            }}
          >
            {TRANSFER_LABELS[embryo.transferRecommendation]}
          </Text>
        </View>
      )}
    </View>
  );
}

// ── CN Plot ──

function CNPlotSection({ cnProfile }: { cnProfile: ChromosomeCN[] }) {
  const W = 320;
  const H = 120;
  const marginLeft = 20;
  const marginRight = 8;
  const marginTop = 12;
  const marginBottom = 20;
  const plotW = W - marginLeft - marginRight;
  const plotH = H - marginTop - marginBottom;
  const maxCN = 4;
  const barWidth = plotW / cnProfile.length;

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={Dna}>Chromosome Copy Number</SectionTitle>
      <View
        style={{
          backgroundColor: "rgba(0,0,0,0.02)",
          borderRadius: 12,
          padding: 8,
          marginTop: 6,
        }}
      >
        <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
          {[0, 1, 2, 3, 4].map((cn) => {
            const y = marginTop + plotH - (cn / maxCN) * plotH;
            return (
              <G key={`grid-${cn}`}>
                <Line
                  x1={marginLeft}
                  y1={y}
                  x2={W - marginRight}
                  y2={y}
                  stroke={cn === 2 ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.04)"}
                  strokeWidth={cn === 2 ? 1.5 : 0.5}
                  strokeDasharray={cn === 2 ? undefined : "3,3"}
                />
                <SvgText
                  x={marginLeft - 4}
                  y={y + 3}
                  fill={EMBER.colors.textFaint}
                  fontSize={8}
                  textAnchor="end"
                >
                  {cn}
                </SvgText>
              </G>
            );
          })}

          {cnProfile.map((point, i) => {
            if (point.chr === "Y" && point.cn === 0) return null;
            const x = marginLeft + i * barWidth + barWidth / 2;
            const y = marginTop + plotH - (point.cn / maxCN) * plotH;
            const color = point.isAbnormal
              ? RESULT_COLORS.aneuploid
              : RESULT_COLORS.euploid;

            return (
              <G key={`cn-${point.chr}`}>
                <Circle cx={x} cy={y} r={3.5} fill={color} opacity={0.85} />
                <SvgText
                  x={x}
                  y={H - 4}
                  fill={
                    point.isAbnormal
                      ? RESULT_COLORS.aneuploid
                      : EMBER.colors.textFaint
                  }
                  fontSize={7}
                  fontWeight={point.isAbnormal ? "bold" : "normal"}
                  textAnchor="middle"
                >
                  {point.chr}
                </SvgText>
              </G>
            );
          })}

          <SvgText
            x={W - marginRight + 2}
            y={marginTop + plotH - (2 / maxCN) * plotH + 3}
            fill={EMBER.colors.textFaint}
            fontSize={7}
          >
            normal
          </SvgText>
        </Svg>
      </View>
    </View>
  );
}

// ── Aneuploidy / Mosaicism Impact ──

function AneuploidySection({ embryo }: { embryo: EmbryoDetailData }) {
  const hasData =
    embryo.aneuploidyImpact ||
    (embryo.mosaicism && embryo.mosaicism.length > 0);
  if (!hasData) return null;

  return (
    <View style={{ marginTop: 16 }}>
      {embryo.aneuploidyImpact && (
        <View style={{ marginTop: 4 }}>
          {embryo.affectedChromosomes && (
            <View style={{ flexDirection: "row", gap: 6, marginBottom: 6 }}>
              {embryo.affectedChromosomes.map((chr) => (
                <Tag key={chr} label={chr} tone="coral" />
              ))}
            </View>
          )}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textMid,
              lineHeight: 18,
            }}
          >
            {embryo.aneuploidyImpact}
          </Text>
        </View>
      )}

      {embryo.mosaicism && embryo.mosaicism.length > 0 && (
        <View style={{ marginTop: 10 }}>
          {embryo.mosaicism.map((m) => (
            <View
              key={m.chromosome}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{ fontSize: 12, color: EMBER.colors.textMid }}
              >
                Chr {m.chromosome}:
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "700",
                  color: RESULT_COLORS.mosaic,
                }}
              >
                ~{m.level}% mosaic
              </Text>
              <Text
                className="font-dm-sans"
                style={{ fontSize: 11, color: EMBER.colors.textFaint }}
              >
                ({m.level <= 40 ? "low-level" : "high-level"})
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

function formatDevelopmentEnum(value: string): string {
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatDevelopmentGrade(
  observation: EmbryoDevelopmentObservation,
): string | null {
  if (observation.gradingNumerical != null && observation.gradingAlphabetical) {
    return `${observation.gradingNumerical}${observation.gradingAlphabetical}`;
  }

  const grades = [observation.icmGrade, observation.teGrade].filter(
    (grade) => grade != null,
  );
  return grades.length > 0 ? grades.join(" / ") : null;
}

function DevelopmentSection({
  observations,
  t,
}: {
  observations: EmbryoDevelopmentObservation[];
  t: TranslationFunction;
}) {
  if (observations.length === 0) return null;

  const orderedObservations = [...observations].sort(
    (left, right) => left.day - right.day,
  );

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={FlaskConical}>
        {t("care.embryo_report.development_section")}
      </SectionTitle>
      <View style={{ gap: 8, marginTop: 8 }}>
        {orderedObservations.map((observation) => {
          const grade = formatDevelopmentGrade(observation);

          return (
            <View
              key={observation.id}
              style={{
                borderRadius: 12,
                borderWidth: 1,
                borderColor: EMBER.colors.border,
                padding: 12,
                gap: 5,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                }}
              >
                {t("care.embryo_report.development_day", {
                  params: { day: String(observation.day) },
                })}
              </Text>
              {observation.stage && (
                <DetailRow
                  label={t("care.embryo_report.development_stage")}
                  value={formatDevelopmentEnum(observation.stage)}
                />
              )}
              {grade && (
                <DetailRow
                  label={t("care.embryo_report.development_grade")}
                  value={grade}
                />
              )}
              {observation.cellCount != null && (
                <DetailRow
                  label={t("care.embryo_report.development_cells")}
                  value={String(observation.cellCount)}
                />
              )}
              {observation.isArrested != null && (
                <DetailRow
                  label={t("care.embryo_report.development_status")}
                  value={t(
                    observation.isArrested
                      ? "care.embryo_report.development_arrested"
                      : "care.embryo_report.development_progressing",
                  )}
                />
              )}
              {observation.notes && (
                <DetailRow
                  label={t("care.embryo_report.development_notes")}
                  value={observation.notes}
                />
              )}
              <DetailRow
                label={t("care.embryo_report.development_source")}
                value={formatDevelopmentEnum(observation.source)}
              />
              <DetailRow
                label={t("care.embryo_report.development_verification")}
                value={formatDevelopmentEnum(observation.verificationStatus)}
              />
            </View>
          );
        })}
      </View>
    </View>
  );
}

// ── Creation Info ──

function CreationInfoSection({ embryo }: { embryo: EmbryoDetailData }) {
  const hasData =
    embryo.fertilisationDate ||
    embryo.fertilisationMethod ||
    embryo.dayOfDevelopment;
  if (!hasData) return null;

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={Calendar}>Creation</SectionTitle>
      <View style={{ gap: 2, marginTop: 6 }}>
        {embryo.fertilisationDate && (
          <DetailRow label="Fertilisation" value={embryo.fertilisationDate} />
        )}
        {embryo.fertilisationMethod && (
          <DetailRow label="Method" value={embryo.fertilisationMethod} />
        )}
        {embryo.dayOfDevelopment && (
          <DetailRow label="Development" value={embryo.dayOfDevelopment} />
        )}
      </View>
    </View>
  );
}

// ── Storage Info ──

function StorageInfoSection({ embryo }: { embryo: EmbryoDetailData }) {
  const hasData = embryo.facility || embryo.freezeDate || embryo.freezingMethod;
  if (!hasData) return null;

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={Snowflake}>Storage</SectionTitle>
      <View style={{ gap: 2, marginTop: 6 }}>
        {embryo.facility && (
          <DetailRow
            label="Facility"
            value={
              embryo.country
                ? `${embryo.facility}, ${embryo.country}`
                : embryo.facility
            }
          />
        )}
        {embryo.freezeDate && (
          <DetailRow label="Freeze date" value={embryo.freezeDate} />
        )}
        {embryo.freezingMethod && (
          <DetailRow label="Method" value={embryo.freezingMethod} />
        )}
        {embryo.storageId && (
          <DetailRow label="Storage ID" value={embryo.storageId} />
        )}
      </View>
    </View>
  );
}

// ── Monogenic Findings ──

function MonogenicSection({ findings }: { findings: MonogenicFinding[] }) {
  const STATUS_LABELS_MONO = {
    unaffected: "Unaffected",
    carrier: "Carrier",
    affected: "Affected",
  };

  return (
    <View
      style={{
        backgroundColor: "rgba(255,255,255,0.35)",
        borderRadius: 14,
        overflow: "hidden",
        marginTop: 6,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          paddingHorizontal: 14,
          paddingVertical: 8,
          backgroundColor: "rgba(0,0,0,0.02)",
        }}
      >
        <Text style={[TH, { flex: 1 }]}>Condition</Text>
        <Text style={[TH, { width: 50, textAlign: "center" }]}>Gene</Text>
        <Text style={[TH, { width: 80, textAlign: "right" }]}>Status</Text>
      </View>

      {findings.map((f) => (
        <View key={f.gene}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              paddingHorizontal: 14,
              paddingVertical: 10,
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "500",
                  color: EMBER.colors.text,
                }}
              >
                {f.condition}
              </Text>
              <Text
                className="font-dm-sans"
                style={{ fontSize: 10, color: EMBER.colors.textFaint }}
              >
                {f.inheritancePattern}
              </Text>
            </View>
            <Text
              className="font-dm-mono"
              style={{
                width: 50,
                textAlign: "center",
                fontSize: 11,
                color: EMBER.colors.textLight,
              }}
            >
              {f.gene}
            </Text>
            <View style={{ width: 80, alignItems: "flex-end" }}>
              <StatusBadge
                label={STATUS_LABELS_MONO[f.status]}
                tone={MONOGENIC_STATUS_TONES[f.status]}
              />
            </View>
          </View>
          {f.clinicalSignificance && (
            <View style={{ paddingHorizontal: 14, paddingBottom: 10 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  color: EMBER.colors.textLight,
                  lineHeight: 16,
                }}
              >
                {f.clinicalSignificance}
              </Text>
            </View>
          )}
        </View>
      ))}
    </View>
  );
}

// ── Polygenic Risk Score Row ──

function PRSRow({ risk }: { risk: PolygenicRisk }) {
  const barW = 200;
  const pctX = (risk.percentile / 100) * barW;
  const ciLowX = risk.ciLow !== undefined ? (risk.ciLow / 100) * barW : pctX;
  const ciHighX = risk.ciHigh !== undefined ? (risk.ciHigh / 100) * barW : pctX;
  const popX = barW * 0.5;

  const dotColor = risk.isElevated
    ? RESULT_COLORS.aneuploid
    : risk.percentile > 75
      ? RESULT_COLORS.mosaic
      : RESULT_COLORS.euploid;

  return (
    <View style={{ marginBottom: 16 }}>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 2,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{ fontSize: 13, fontWeight: "500", color: EMBER.colors.text }}
        >
          {risk.condition}
        </Text>
        {risk.isElevated && <StatusBadge label="Elevated" tone="coral" />}
      </View>

      <View style={{ flexDirection: "row", gap: 12, marginBottom: 6 }}>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 11, color: EMBER.colors.textLight }}
        >
          {risk.absoluteRisk} lifetime risk
        </Text>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 11, color: EMBER.colors.textFaint }}
        >
          vs. {risk.populationRisk} avg
        </Text>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 11, color: EMBER.colors.textFaint }}
        >
          {risk.percentile}th percentile
        </Text>
      </View>

      <Svg width="100%" height={24} viewBox={`0 0 ${barW} 24`}>
        <Rect
          x={0}
          y={10}
          width={barW}
          height={4}
          rx={2}
          fill="rgba(0,0,0,0.06)"
        />
        <Line
          x1={popX}
          y1={4}
          x2={popX}
          y2={20}
          stroke="rgba(0,0,0,0.15)"
          strokeWidth={1}
          strokeDasharray="2,2"
        />
        <Line
          x1={ciLowX}
          y1={12}
          x2={ciHighX}
          y2={12}
          stroke={dotColor}
          strokeWidth={2}
          opacity={0.3}
        />
        <Line
          x1={ciLowX}
          y1={8}
          x2={ciLowX}
          y2={16}
          stroke={dotColor}
          strokeWidth={1}
          opacity={0.3}
        />
        <Line
          x1={ciHighX}
          y1={8}
          x2={ciHighX}
          y2={16}
          stroke={dotColor}
          strokeWidth={1}
          opacity={0.3}
        />
        <Circle cx={pctX} cy={12} r={5} fill={dotColor} />
      </Svg>
    </View>
  );
}

// ── Transfer Info ──

function TransferInfoSection({ embryo }: { embryo: EmbryoDetailData }) {
  const hasData = embryo.transferDate || embryo.transferClinic;
  if (!hasData) return null;

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={HeartPulse}>Transfer</SectionTitle>
      <View style={{ gap: 2, marginTop: 6 }}>
        {embryo.transferDate && (
          <DetailRow label="Date" value={embryo.transferDate} />
        )}
        {embryo.transferClinic && (
          <DetailRow label="Clinic" value={embryo.transferClinic} />
        )}
        {embryo.transferProtocol && (
          <DetailRow label="Protocol" value={embryo.transferProtocol} />
        )}
        {embryo.embryologist && (
          <DetailRow label="Embryologist" value={embryo.embryologist} />
        )}
      </View>
    </View>
  );
}

// ── Outcome Info ──

function OutcomeInfoSection({ embryo }: { embryo: EmbryoDetailData }) {
  if (!embryo.outcome) return null;

  const label = OUTCOME_LABELS[embryo.outcome] ?? embryo.outcome;
  const color = OUTCOME_COLORS[embryo.outcome] ?? EMBER.colors.textMid;

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={Baby}>Outcome</SectionTitle>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          paddingVertical: 8,
          paddingHorizontal: 12,
          borderRadius: 10,
          backgroundColor: `${color}12`,
          marginTop: 6,
        }}
      >
        <View
          style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: color,
          }}
        />
        <Text
          className="font-dm-sans"
          style={{ fontSize: 12, fontWeight: "600", color }}
        >
          {label}
        </Text>
        {embryo.outcomeDate && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.textFaint,
              marginLeft: "auto",
            }}
          >
            {embryo.outcomeDate}
          </Text>
        )}
      </View>
      {embryo.outcomeNotes && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.textLight,
            lineHeight: 16,
            marginTop: 6,
          }}
        >
          {embryo.outcomeNotes}
        </Text>
      )}
    </View>
  );
}

// ── Comments ──

function CommentsSection({
  notes,
  labComments,
}: {
  notes?: string;
  labComments?: string;
}) {
  if (!notes && !labComments) return null;
  const text = [labComments, notes].filter(Boolean).join("\n\n");

  return (
    <View
      style={{
        backgroundColor: "rgba(0,0,0,0.02)",
        borderRadius: 10,
        padding: 10,
        marginTop: 16,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontStyle: "italic",
          color: EMBER.colors.textLight,
          lineHeight: 16,
        }}
      >
        {text}
      </Text>
    </View>
  );
}

// ── Embryo placeholder image ──

const EMBRYO_PLACEHOLDER =
  require("@louise/moon-ui/assets/images/onboarding-bg.jpg") as number;

// ── Main Component ──

function EmbryoDetailContent({
  embryo,
  onClose,
  onDownloadReport,
  onNavigateToCryoVault,
}: {
  embryo: EmbryoDetailData;
  onClose: () => void;
  onDownloadReport?: () => void;
  onNavigateToCryoVault?: () => void;
}) {
  const translateY = useSharedValue(0);
  const opacity = useSharedValue(1);

  const onScrollHaptics = useScrollHaptics();
  const t = useTranslations();
  const capturePress = usePressCapture("EmbryoDetailContent");

  const handleClose = useCallback(() => {
    opacity.value = withTiming(0, { duration: 200 });
    translateY.value = withSpring(80, springs.sheet, () => {
      runOnJS(onClose)();
    });
  }, [onClose, opacity, translateY]);

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }));

  const hasExtended = !!embryo.monogenicFindings || !!embryo.polygenicRisks;

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <SafeAreaView style={{ flex: 1 }}>
          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={onScrollHaptics}
            scrollEventThrottle={16}
          >
            <View
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* Close button */}
              <View
                style={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
              >
                <Pressable
                  onPress={() => {
                    capturePress("close_embryo_detail");
                    handleClose();
                  }}
                  hitSlop={8}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </Pressable>
              </View>

              {/* Hero visual */}
              <View
                style={{
                  alignItems: "center",
                  paddingTop: 8,
                  paddingBottom: 16,
                }}
              >
                {embryo.imageUri ? (
                  <Image
                    source={{ uri: embryo.imageUri }}
                    style={{ width: 96, height: 96, borderRadius: 48 }}
                    resizeMode="cover"
                  />
                ) : (
                  <View
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: 48,
                      backgroundColor: "rgba(0,0,0,0.03)",
                      borderWidth: 1,
                      borderColor: "rgba(0,0,0,0.06)",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Text style={{ fontSize: 36 }}>
                      {embryo.sex === "XX" ? "\u2640" : "\u2642"}
                    </Text>
                  </View>
                )}
              </View>

              {/* Title + nickname */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 24,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  lineHeight: 30,
                  textAlign: "center",
                }}
              >
                {embryo.nickname || `Embryo ${embryo.embryoNumber}`}
              </Text>
              {embryo.nickname && (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    color: EMBER.colors.textFaint,
                    textAlign: "center",
                    marginTop: 2,
                  }}
                >
                  Embryo #{embryo.embryoNumber}
                  {embryo.embryoId ? ` · ${embryo.embryoId}` : ""}
                </Text>
              )}
              {!embryo.nickname && embryo.embryoId && (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    color: EMBER.colors.textFaint,
                    textAlign: "center",
                    marginTop: 2,
                  }}
                >
                  {embryo.embryoId}
                </Text>
              )}

              {/* Rename hint */}
              <Pressable
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  marginTop: 6,
                }}
              >
                <Pencil size={10} color={EMBER.colors.textFaint} />
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 11, color: EMBER.colors.textFaint }}
                >
                  {embryo.nickname ? "Edit nickname" : "Add nickname"}
                </Text>
              </Pressable>

              {/* Badge row */}
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 14,
                  justifyContent: "center",
                }}
              >
                {/* Result badge */}
                {embryo.result && (
                  <StatusBadge
                    label={RESULT_LABELS[embryo.result] ?? embryo.result}
                    tone={RESULT_TONES[embryo.result] ?? "neutral"}
                  />
                )}

                {/* Status badge */}
                {embryo.status && (
                  <StatusBadge
                    label={STATUS_LABELS[embryo.status] ?? embryo.status}
                    tone={STATUS_TONES[embryo.status] ?? "neutral"}
                  />
                )}

                {/* Grade tag */}
                {embryo.grade && <Tag label={embryo.grade} variant="outline" />}

                {/* Day of development tag */}
                {embryo.dayOfDevelopment && (
                  <Tag label={embryo.dayOfDevelopment} variant="outline" />
                )}

                {/* Sex tag */}
                <Tag
                  label={embryo.sex}
                  leading={
                    <Text style={{ fontSize: 10 }}>
                      {embryo.sex === "XX" ? "\u2640" : "\u2642"}
                    </Text>
                  }
                  variant="outline"
                />

                {/* Genomics provider tag */}
                {embryo.genomicsProvider && (
                  <Tag
                    icon={Dna}
                    label={embryo.genomicsProvider}
                    variant="outline"
                  />
                )}
              </View>

              {/* ── Sections ── */}

              {/* Genetic Overview */}
              <GeneticOverviewSection embryo={embryo} />

              {/* CN Plot */}
              {embryo.cnProfile && embryo.cnProfile.length > 0 && (
                <CNPlotSection cnProfile={embryo.cnProfile} />
              )}

              {/* Aneuploidy / mosaicism impact */}
              <AneuploidySection embryo={embryo} />

              {/* Embryo development history */}
              {embryo.developmentObservations && (
                <DevelopmentSection
                  observations={embryo.developmentObservations}
                  t={t}
                />
              )}

              {/* Creation */}
              <CreationInfoSection embryo={embryo} />

              {/* Storage */}
              <StorageInfoSection embryo={embryo} />

              {/* Monogenic Findings */}
              {embryo.monogenicFindings &&
                embryo.monogenicFindings.length > 0 && (
                  <View style={{ marginTop: 24 }}>
                    <SectionTitle icon={Dna}>Monogenic Findings</SectionTitle>
                    {embryo.genomicsProvider && (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 10,
                          color: EMBER.colors.textFaint,
                          marginBottom: 4,
                        }}
                      >
                        via {embryo.genomicsProvider}
                      </Text>
                    )}
                    <MonogenicSection findings={embryo.monogenicFindings} />
                  </View>
                )}

              {/* Polygenic Risk Scores */}
              {embryo.polygenicRisks && embryo.polygenicRisks.length > 0 && (
                <View style={{ marginTop: 24 }}>
                  <SectionTitle icon={FlaskConical}>
                    Polygenic Risk Scores
                  </SectionTitle>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "flex-start",
                      gap: 6,
                      backgroundColor: "rgba(96, 165, 250, 0.06)",
                      borderRadius: 10,
                      padding: 10,
                      marginTop: 6,
                      marginBottom: 14,
                    }}
                  >
                    <Info size={12} color="#60A5FA" style={{ marginTop: 1 }} />
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 11,
                        color: EMBER.colors.textLight,
                        lineHeight: 16,
                        flex: 1,
                      }}
                    >
                      Polygenic scores reflect genetic predisposition, not a
                      diagnosis. The dashed line marks population average (50th
                      percentile). Whiskers show the 95% confidence interval.
                    </Text>
                  </View>
                  {embryo.polygenicRisks.map((risk) => (
                    <PRSRow key={risk.condition} risk={risk} />
                  ))}
                </View>
              )}

              {/* Transfer info */}
              <TransferInfoSection embryo={embryo} />

              {/* Outcome */}
              <OutcomeInfoSection embryo={embryo} />

              {/* Comments */}
              <CommentsSection
                notes={embryo.notes}
                labComments={embryo.labComments}
              />

              {onNavigateToCryoVault && (
                <View style={{ marginTop: 20 }}>
                  <WarmButton
                    title={t("care.embryo_report.open_cryo_vault")}
                    onPress={onNavigateToCryoVault}
                  />
                </View>
              )}

              {/* Download report */}
              {onDownloadReport && (
                <View style={{ marginTop: 20 }}>
                  <WarmButton
                    title={t("care.embryo_report.download_full_report")}
                    onPress={onDownloadReport}
                  />
                </View>
              )}
            </View>
          </Animated.ScrollView>
        </SafeAreaView>
      </Animated.View>
    </View>
  );
}

export default function EmbryoDetailSheet({
  open,
  onClose,
  embryo,
  onDownloadReport,
  onNavigateToCryoVault,
}: EmbryoDetailSheetProps) {
  if (!embryo) return null;

  return (
    <Modal
      visible={open}
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      <EmbryoDetailContent
        embryo={embryo}
        onClose={onClose}
        onDownloadReport={onDownloadReport}
        onNavigateToCryoVault={onNavigateToCryoVault}
      />
    </Modal>
  );
}

// Re-export sub-components for use in other contexts (e.g. comparison tables)
export { CNPlotSection, MonogenicSection, PRSRow };
